import { Controller } from "@hotwired/stimulus"

// QAN-385: Compliance Dashboard interactions.
//
// Both Table and Card views are rendered server-side; this controller toggles
// between them (remembered in localStorage), saves the inline requirement
// dropdowns without a full reload, and drives the modal-backed actions (add note,
// place on hold, change finish date, send completion) by POST/PATCHing the
// /admin/compliance_clients/:id endpoints. (Consent is read-only.)
export default class extends Controller {
  static targets = ["tableView", "cardView", "tableButton", "cardButton", "modal", "modalBody"]

  static STORAGE_KEY = "complianceDashboardView"

  connect() {
    const saved = window.localStorage.getItem(this.constructor.STORAGE_KEY)
    if (saved === "card") {
      this.showCard()
    } else {
      this.showTable()
    }
  }

  // ── View toggle ──

  showTable() {
    if (!this.hasTableViewTarget) return
    this.tableViewTarget.classList.remove("hidden")
    if (this.hasCardViewTarget) this.cardViewTarget.classList.add("hidden")
    this.setActive(this.tableButtonTarget, this.cardButtonTarget)
    window.localStorage.setItem(this.constructor.STORAGE_KEY, "table")
  }

  showCard() {
    if (!this.hasCardViewTarget) return
    this.cardViewTarget.classList.remove("hidden")
    if (this.hasTableViewTarget) this.tableViewTarget.classList.add("hidden")
    this.setActive(this.cardButtonTarget, this.tableButtonTarget)
    window.localStorage.setItem(this.constructor.STORAGE_KEY, "card")
  }

  // QAN-531: moves the design system's `active` class. This used to swap
  // bg-indigo-600/text-white against bg-white/text-gray-700, which is why the
  // toggle rendered as an indigo pill on a page where every other control is the
  // system's square navy one. admin_design.scss owns `.view-toggle-btn.active`,
  // and the buttons now come from admin/shared/_view_toggle.
  setActive(active, inactive) {
    if (!active || !inactive) return
    active.classList.add("active")
    inactive.classList.remove("active")
  }

  // ── Inline requirement save ──
  //
  // Each requirement dropdown carries data-url (the update_service endpoint) and
  // data-field (the column). On change we POST the single field so the compliance
  // BusinessLoanService row stays the single source of truth. (Consent is a
  // read-only indicator and is not editable from the dashboard.)
  async saveField(event) {
    const el = event.target
    const url = el.dataset.url
    const field = el.dataset.field
    if (!url || !field) return

    const row = el.closest("[data-compliance-row]")
    const previousStatus = row ? row.dataset.status : null

    const value = el.type === "checkbox" ? el.checked : el.value
    const feedback = this.feedbackFor(el)
    this.setFeedback(feedback, "Saving…", "text-gray-400")

    const result = await this.send(url, "POST", { business_loan_service: { [field]: value } })
    if (result) {
      this.setFeedback(feedback, "Saved", "text-green-600")
      // SOP auto-transitions can change the row's status (e.g. checking the last
      // requirement box auto-completes the client). When that happens the row
      // belongs in a different kanban column / status badge — reload to reflect
      // the move rather than leave the UI out of sync.
      if (previousStatus && result.status && result.status !== previousStatus) {
        window.location.reload()
      }
    } else {
      this.setFeedback(feedback, "Error", "text-red-600")
    }
  }

  // ── Card view: Stage dropdown + Save ──
  //
  // The Save button POSTs the sibling Stage <select> to update_service. The
  // server syncs status to the new stage, so the card moves to the matching
  // kanban column — reload to reflect the move.
  async saveStage(event) {
    const button = event.currentTarget
    const url = button.dataset.url
    const card = button.closest("[data-compliance-row]")
    const select = card ? card.querySelector("[data-compliance-stage-select]") : null
    if (!url || !select) return

    const feedback = this.feedbackFor(button)
    this.setFeedback(feedback, "Saving…", "text-gray-400")

    const result = await this.send(url, "POST", { business_loan_service: { stage: select.value } })
    if (result) {
      window.location.reload()
    } else {
      this.setFeedback(feedback, "Error", "text-red-600")
    }
  }

  // ── Table view: Status dropdown ──
  //
  // Per the SOP "Stage drives Status", each option carries the SOP stage it maps
  // to (data-stage). Changing the status POSTs that stage to update_service so
  // status and stage stay in lockstep; the row then belongs in a different
  // bucket, so reload to reflect the move.
  //
  // "On Hold" is the one exception: placing a hold requires a logged reason, so
  // the On Hold option is not saved directly. Instead the select is reverted to
  // the row's persisted status and the same reason-gated hold modal the On Hold
  // checkbox uses is opened; the hold is committed only when that form submits.
  async saveStatus(event) {
    const select = event.currentTarget
    const url = select.dataset.url
    const option = select.options[select.selectedIndex]
    const stage = option ? option.dataset.stage : null
    if (!url || !stage) return

    if (option.hasAttribute("data-compliance-hold-option")) {
      this.revertStatusSelect(select)
      this.openHoldModalFromTemplate(select.dataset.holdModalTemplate)
      return
    }

    const feedback = this.feedbackFor(select)
    this.setFeedback(feedback, "Saving…", "text-gray-400")

    const result = await this.send(url, "POST", { business_loan_service: { stage: stage } })
    if (result) {
      window.location.reload()
    } else {
      this.setFeedback(feedback, "Error", "text-red-600")
    }
  }

  // Restore the status <select> to the row's persisted stage, so a cancelled
  // "On Hold" pick doesn't leave a stale selection. The row carries its rendered
  // compliance_stage_label (which distinguishes Onboarding from In Process).
  revertStatusSelect(select) {
    const row = select.closest("[data-compliance-row]")
    const stage = row ? row.dataset.stage : null
    if (stage) select.value = stage
  }

  openHoldModalFromTemplate(templateId) {
    this.showModalTemplate(templateId)
  }

  // ── Card view: Actions dropdown ──

  toggleActions(event) {
    event.preventDefault()
    const wrapper = event.currentTarget.closest("[data-compliance-actions]")
    const menu = wrapper ? wrapper.querySelector("[data-compliance-actions-menu]") : null
    if (!menu) return
    // Close any other open menus first.
    this.element.querySelectorAll("[data-compliance-actions-menu]").forEach((m) => {
      if (m !== menu) m.classList.add("hidden")
    })
    menu.classList.toggle("hidden")
  }

  // ── Card view: per-column filter + sort ──

  filterColumn(event) {
    const input = event.currentTarget
    const status = input.dataset.complianceColumnFilter
    const term = input.value.trim().toLowerCase()
    const body = this.element.querySelector(`[data-compliance-column-body="${status}"]`)
    if (!body) return
    body.querySelectorAll("[data-compliance-card]").forEach((card) => {
      const name = card.dataset.clientName || ""
      card.classList.toggle("hidden", term !== "" && !name.includes(term))
    })
  }

  sortColumn(event) {
    const select = event.currentTarget
    const status = select.dataset.complianceColumnSort
    const body = this.element.querySelector(`[data-compliance-column-body="${status}"]`)
    if (!body) return

    const cards = Array.from(body.querySelectorAll("[data-compliance-card]"))
    const [key, dir] = (select.value || "").split("-")
    if (!key) return

    const value = (card) => {
      if (key === "name") return card.dataset.clientName || ""
      if (key === "stage") return parseInt(card.dataset.daysInStage || "0", 10)
      if (key === "finish") return card.dataset.finishDate || ""
      return ""
    }

    cards.sort((a, b) => {
      const va = value(a)
      const vb = value(b)
      let cmp
      if (typeof va === "number") {
        cmp = va - vb
      } else {
        cmp = va.localeCompare(vb)
      }
      return dir === "desc" ? -cmp : cmp
    })

    cards.forEach((card) => body.appendChild(card))
  }

  // ── Modal control ──

  openModal(event) {
    event.preventDefault()
    this.showModalTemplate(event.currentTarget.dataset.modalTemplate)
  }

  // Table view: the Actions column is a native <select> rather than a popover.
  // The table wrapper is `#table-view` (overflow-x: auto), which clips any
  // absolutely-positioned menu — the same bug that rendered the card view's
  // floating menu as an unreadable sliver. A native select's popup is drawn
  // outside the scroll container, so it can't be clipped. The chosen option
  // carries the modal template id; the select resets to its placeholder so it
  // reads as an action trigger, not as persisted state.
  runAction(event) {
    const select = event.currentTarget
    const option = select.options[select.selectedIndex]
    select.selectedIndex = 0
    if (!option || !option.dataset.modalTemplate) return

    this.showModalTemplate(option.dataset.modalTemplate)
  }

  // Single entry point for every modal opener: clones a row's hidden <template>
  // into the shared modal shell. Callers pass a possibly-missing dataset value,
  // so a blank id is a no-op rather than a lookup for id="undefined".
  showModalTemplate(templateId) {
    if (!templateId) return

    const template = document.getElementById(templateId)
    if (!template || !this.hasModalTarget || !this.hasModalBodyTarget) return

    this.modalBodyTarget.innerHTML = template.innerHTML
    this.modalTarget.classList.remove("hidden")
  }

  // The "Placed Client On Hold" checkbox never saves directly: placing/removing a
  // hold requires a logged reason, so the click is intercepted and the reason-gated
  // hold modal is opened instead. The checkbox itself is reverted to the persisted
  // state; the actual toggle happens when the modal form is submitted (page reload).
  openHoldModal(event) {
    const checkbox = event.currentTarget
    event.preventDefault()
    checkbox.checked = !checkbox.checked

    this.showModalTemplate(checkbox.dataset.modalTemplate)
  }

  closeModal(event) {
    if (event) event.preventDefault()
    if (!this.hasModalTarget) return
    this.modalTarget.classList.add("hidden")
    if (this.hasModalBodyTarget) this.modalBodyTarget.innerHTML = ""
  }

  // ── Modal form submit (note / on-hold / finish-date / completion) ──

  async submitModal(event) {
    event.preventDefault()
    const form = event.currentTarget
    const url = form.action
    const method = (form.dataset.method || "post").toUpperCase()
    const errorEl = form.querySelector("[data-modal-error]")

    const payload = {}
    form.querySelectorAll("[name]").forEach((input) => {
      if (input.type === "checkbox") {
        payload[input.name] = input.checked
      } else {
        payload[input.name] = input.value
      }
    })

    const result = await this.send(url, method, payload)
    if (result) {
      window.location.reload()
    } else if (errorEl) {
      errorEl.textContent = "Something went wrong. Please try again."
      errorEl.classList.remove("hidden")
    }
  }

  // ── Shared fetch helper ──

  async send(url, method, body) {
    const csrfMeta = document.querySelector('meta[name="csrf-token"]')
    const csrfToken = csrfMeta ? csrfMeta.content : ""

    try {
      const response = await fetch(url, {
        method: method,
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify(body)
      })
      if (!response.ok) return null
      return await response.json()
    } catch {
      return null
    }
  }

  feedbackFor(el) {
    const row = el.closest("[data-compliance-row]")
    return row ? row.querySelector("[data-field-feedback]") : null
  }

  setFeedback(el, text, className) {
    if (!el) return
    el.textContent = text
    el.className = `text-xs ${className}`
    if (text === "Saved") {
      window.setTimeout(() => { if (el.textContent === "Saved") el.textContent = "" }, 2000)
    }
  }
}
